import { useState, useRef, useCallback, MutableRefObject } from 'react';
import { t } from '../i18n/helpers';
import { WebSocketAdapter } from '../utils/WebSocketAdapter';
import RoomSessionStorage from '../utils/sessionStorage';
import { navigateToRoot } from '../utils/entryRoutes';

/** Minimal room state shape needed to detect "already in room" and avoid duplicate reconnection emit */
interface RoomStateRef {
  roomName: string;
}

interface UseReconnectionOptions {
  socket: WebSocketAdapter | null;
  isLeavingRef: MutableRefObject<boolean>;
  /** Ref to current room state. If we already have state for the saved room, skip emitting to avoid duplicate join/get-room-state. */
  roomStateRef: MutableRefObject<RoomStateRef | null>;
  onMaxAttemptsReached: (isHost: boolean) => void;
  showToast: (message: string, type?: 'success' | 'error' | 'warning' | 'info', duration?: number) => void;
  setConnected: (value: boolean) => void;
  setShowJoinRoom: (value: boolean) => void;
  setRoomState: (value: any) => void;
}

interface UseReconnectionReturn {
  reconnecting: boolean;
  reconnectAttempts: number;
  reconnectTimeoutRef: MutableRefObject<NodeJS.Timeout | null>;
  startReconnection: () => void;
  clearReconnection: () => void;
  setReconnecting: (value: boolean) => void;
  setReconnectAttempts: (value: number) => void;
}

const MAX_RECONNECT_ATTEMPTS = 5;
const INITIAL_DELAY = 2000; // 2 seconds before first attempt
const BASE_DELAY = 3000; // Base delay between attempts (3s, 4s, 5s, 6s, 7s)

/**
 * Custom hook to handle WebSocket reconnection logic
 */
function normalizeRoomName(name: string | undefined): string {
  return (name ?? '').toUpperCase().trim().slice(0, 4);
}

export function useReconnection({
  socket,
  isLeavingRef,
  roomStateRef,
  onMaxAttemptsReached,
  showToast,
  setConnected,
  setShowJoinRoom,
  setRoomState,
}: UseReconnectionOptions): UseReconnectionReturn {
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearReconnection = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setReconnecting(false);
    setReconnectAttempts(0);
  }, []);

  const attemptReconnect = useCallback(
    (attempt: number) => {
      if (attempt >= MAX_RECONNECT_ATTEMPTS) {
        console.log('Max reconnection attempts reached, giving up');
        clearReconnection();
        
        const hadHostToken = !!RoomSessionStorage.getHostToken();
        RoomSessionStorage.clearAll();
        setRoomState(null);
        setConnected(true);
        
        if (hadHostToken) {
          setShowJoinRoom(false);
          navigateToRoot(true);
        } else {
          setShowJoinRoom(true);
        }
        
        onMaxAttemptsReached(hadHostToken);
        
        showToast(
          hadHostToken ? t('toasts.connectionLostHost') : t('toasts.connectionLostPlayer'),
          'error'
        );
        return;
      }

      setReconnectAttempts(attempt + 1);
      
      const savedData = RoomSessionStorage.getSavedRoomData();
      const savedRoomName = savedData.roomName;
      const savedIsHost = savedData.isHost;
      const savedHostToken = savedData.hostToken;
      
      // Check if room is expired before attempting reconnection
      const isExpired = savedData.expiresAt ? Date.now() > savedData.expiresAt : false;
      
      if (isExpired) {
        // Room expired during reconnection attempts - clear data and stop
        console.log(`Room ${savedRoomName} expired during reconnection, clearing data`);
        clearReconnection();
        RoomSessionStorage.clearAll();
        setRoomState(null);
        setConnected(true);
        setShowJoinRoom(false);
        navigateToRoot(true);
        showToast(t('toasts.roomExpired'), 'warning');
        return;
      }
      
      console.log(`Reconnection attempt ${attempt + 1}/${MAX_RECONNECT_ATTEMPTS} (${savedIsHost ? 'host' : 'player'})`);

      reconnectTimeoutRef.current = setTimeout(() => {
        if (!socket) {
          attemptReconnect(attempt + 1);
          return;
        }

        if (socket.connected) {
          // Double-check expiration before reconnecting
          const currentSavedData = RoomSessionStorage.getSavedRoomData();
          const currentIsExpired = currentSavedData.expiresAt ? Date.now() > currentSavedData.expiresAt : false;

          if (currentIsExpired) {
            console.log(`Room ${currentSavedData.roomName} expired, stopping reconnection`);
            clearReconnection();
            RoomSessionStorage.clearAll();
            setRoomState(null);
            setConnected(true);
            setShowJoinRoom(false);
            navigateToRoot(true);
            showToast(t('toasts.roomExpired'), 'warning');
            return;
          }

          // Skip emit if we already have room state for this room (e.g. handleConnect already ran and restored)
          const currentRoomName = roomStateRef.current?.roomName;
          if (currentRoomName && savedRoomName && normalizeRoomName(currentRoomName) === normalizeRoomName(savedRoomName)) {
            console.log('Already have room state for this room, skipping duplicate reconnection emit');
            clearReconnection();
            return;
          }

          console.log('Reconnected successfully, attempting to restore room state...');
          clearReconnection();

          // Use hostToken (not sessionStorage isHost) to decide host reconnection - isHost is shared and can be overwritten by the other tab
          if (savedHostToken && savedRoomName) {
            socket.emit('get-room-state', { roomName: savedRoomName, hostToken: savedHostToken });
          } else if (savedRoomName && savedData.playerName) {
            // Player reconnection: emit join-room (not get-room-state) because the player was
            // removed from the room on disconnect and needs to be re-added with their name/score
            socket.emit('join-room', {
              roomName: savedRoomName,
              playerName: savedData.playerName,
              avatar: savedData.avatar || undefined,
            });
          }
        } else {
          // Still not connected, try again
          attemptReconnect(attempt + 1);
        }
      }, BASE_DELAY + attempt * 1000); // 3s, 4s, 5s, 6s, 7s delays
    },
    [socket, roomStateRef, clearReconnection, onMaxAttemptsReached, showToast, setConnected, setShowJoinRoom, setRoomState]
  );

  const startReconnection = useCallback(() => {
    if (isLeavingRef.current) {
      return; // Don't reconnect if user intentionally left
    }

    const savedData = RoomSessionStorage.getSavedRoomData();
    const savedRoomName = savedData.roomName;
    
    // Check if room is expired before attempting reconnection
    const isExpired = savedData.expiresAt ? Date.now() > savedData.expiresAt : false;
    
    if (!savedRoomName) {
      // No room in session storage - room was likely destroyed
      // Go to start screen instead of trying to reconnect
      setShowJoinRoom(false);
      navigateToRoot(true);
      setConnected(true);
      return;
    }
    
    if (isExpired) {
      // Room is expired - clear data and go to start screen
      console.log(`Room ${savedRoomName} has expired, clearing data and showing start screen`);
      RoomSessionStorage.clearAll();
      setRoomState(null);
      setShowJoinRoom(false);
      navigateToRoot(true);
      setConnected(true);
      return;
    }

    console.log(`Disconnected while in room ${savedRoomName}, attempting to reconnect...`);
    setReconnecting(true);
    setReconnectAttempts(0);

    // Clear any existing timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Start first reconnection attempt after initial delay
    reconnectTimeoutRef.current = setTimeout(() => {
      attemptReconnect(0);
    }, INITIAL_DELAY);
  }, [isLeavingRef, attemptReconnect, setShowJoinRoom, setConnected, setRoomState]);

  return {
    reconnecting,
    reconnectAttempts,
    reconnectTimeoutRef,
    startReconnection,
    clearReconnection,
    setReconnecting,
    setReconnectAttempts,
  };
}
